/**
 * stockReport — agregações PURAS do Relatório de Estoque.
 *
 * Fora do componente de propósito (mesmo padrão de `paymentsReport.ts` e
 * `stock.ts`): tudo aqui é função pura, testável sem renderizar nada.
 *
 * Três regras que este módulo existe para não deixar quebrar em silêncio:
 *
 * 1. **A unidade estocável é a VARIAÇÃO, nunca o pai.** Quem chama já filtra com
 *    `isStockableUnit` (o `useStock` faz isso) — aqui nunca somamos filhas num
 *    pai só, senão "Camiseta 12 un." esconderia que o P está zerado e o G
 *    sobrando.
 * 2. **`min_stock` nulo significa "sem alerta", não zero.** Toda checagem de
 *    estoque baixo passa por `isLowStock` (src/lib/stock.ts), que já trata isso.
 * 3. **Saldo NEGATIVO é estado legítimo** (o app permite de propósito: a baixa
 *    de uma venda pode passar do saldo contado). Nunca é erro nem é tratado
 *    como zero — tem contador e balde de alerta próprios.
 *
 * ⚠️ Sobre valor: há DOIS valores e eles não são a mesma coisa.
 *  - `salesValue` = saldo × `price` (preço de VENDA) = **potencial de venda**.
 *  - `investedValue` = saldo × `cost` (custo de compra) = **capital investido**.
 * `products.cost` é NULLABLE, então parte do estoque pode não ter custo. Ausência
 * NUNCA é tratada como zero (isso subestimaria o investido em silêncio): as
 * unidades sem custo ficam fora da soma e são CONTADAS em `missingCostCount`,
 * para a tela poder dizer quantas ficaram de fora.
 */
import { isLowStock, STOCK_REASON_LABELS } from '@/lib/stock';
import { productUnitCost } from '@/lib/cost';
import type { StockReason } from '@/types/database';

/**
 * O mínimo que estas funções precisam de uma unidade estocável — satisfeito
 * tanto pela linha crua de `products` quanto pelos tipos concretos da UI.
 */
export interface StockUnitRow {
  id: string;
  name?: string | null;
  /** Preenchido quando a linha é uma VARIAÇÃO. */
  variant_name?: string | null;
  parent_product_id?: string | null;
  stock_quantity?: number | null;
  /** `null` = sem alerta de mínimo (não é zero!). */
  min_stock?: number | null;
  /** Preço de VENDA (não é custo). */
  price?: number | null;
  /** Custo de compra/produção. `null` = NÃO informado (nunca zero!). */
  cost?: number | null;
  unit?: string | null;
  category?: string | null;
}

export const STOCK_REPORT_SORT_FIELDS = [
  { value: 'name', label: 'Produto', serverColumn: 'name' },
  { value: 'balance', label: 'Saldo', serverColumn: 'stock_quantity' },
  { value: 'price', label: 'Preço de venda', serverColumn: 'price' },
  { value: 'cost', label: 'Custo', serverColumn: 'cost' },
] as const;

export type StockReportSortField = (typeof STOCK_REPORT_SORT_FIELDS)[number]['value'];
export type StockReportSortDirection = 'asc' | 'desc';

export interface StockReportSort {
  field: StockReportSortField;
  direction: StockReportSortDirection;
}

export const DEFAULT_STOCK_REPORT_SORT: StockReportSort = {
  field: 'name',
  direction: 'asc',
};

export function stockSortServerColumn(field: StockReportSortField): string {
  return STOCK_REPORT_SORT_FIELDS.find((option) => option.value === field)?.serverColumn ?? 'name';
}

export function sortStockUnits(
  units: readonly StockUnitRow[],
  sort: StockReportSort,
): StockUnitRow[] {
  const direction = sort.direction === 'asc' ? 1 : -1;
  return units
    .map((unit, index) => ({ unit, index }))
    .sort((left, right) => {
      const leftRaw = sort.field === 'name'
        ? stockUnitLabel(left.unit)
        : sort.field === 'balance'
          ? left.unit.stock_quantity
          : left.unit[sort.field];
      const rightRaw = sort.field === 'name'
        ? stockUnitLabel(right.unit)
        : sort.field === 'balance'
          ? right.unit.stock_quantity
          : right.unit[sort.field];
      const leftMissing = leftRaw == null || leftRaw === '';
      const rightMissing = rightRaw == null || rightRaw === '';
      if (leftMissing !== rightMissing) return leftMissing ? 1 : -1;
      let compared = 0;
      if (sort.field === 'name') {
        compared = stockUnitLabel(left.unit).localeCompare(
          stockUnitLabel(right.unit),
          'pt-BR',
          { sensitivity: 'base' },
        );
      } else {
        const leftValue = sort.field === 'balance'
          ? balanceOf(left.unit)
          : Number(left.unit[sort.field]);
        const rightValue = sort.field === 'balance'
          ? balanceOf(right.unit)
          : Number(right.unit[sort.field]);
        compared = leftValue - rightValue;
      }
      return compared * direction || left.index - right.index;
    })
    .map(({ unit }) => unit);
}

/** Linha da razão `stock_movements` no recorte do período. */
export interface StockMovementRow {
  id?: string;
  product_id: string;
  /** Delta assinado: entrada +, saída −. */
  delta: number;
  balance_after?: number | null;
  reason: StockReason;
  /** timestamptz ISO. */
  created_at: string;
  note?: string | null;
}

/**
 * Rótulos curtos para o eixo do gráfico. Os completos
 * (`STOCK_REASON_LABELS`) não cabem numa tela de 360px — "Baixa por ordem de
 * serviço" viraria um borrão. A tabela abaixo do gráfico usa o rótulo completo.
 *
 * MEDIDO em 360px: com os 6 motivos, cada categoria fica com ~44px de eixo.
 * "Orçamento" ocupava ~50px e SOBREPUNHA o vizinho, por isso vai abreviado —
 * nenhum outro rótulo passa de 40px.
 */
export const SHORT_REASON_LABELS: Record<StockReason, string> = {
  manual_in: 'Entrada',
  manual_out: 'Saída',
  adjustment: 'Ajuste',
  quote_sale: 'Orçam.',
  service_order_sale: 'OS',
  purchase: 'Compra',
  // 7º motivo (grupo 7): com ele o eixo cai de ~44px para ~38px por categoria.
  // "Devol." mede ~32px pelo mesmo cálculo que abreviou "Orçamento" → cabe.
  sale_return: 'Devol.',
};

/** Ordem canônica de exibição dos motivos (estável entre sessões). */
const REASON_ORDER = Object.keys(STOCK_REASON_LABELS) as StockReason[];

/** Número seguro: `null`/`undefined`/texto inválido viram 0, nunca NaN. */
function num(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** Saldo atual da unidade (pode ser negativo — é legítimo). */
export function balanceOf(unit: StockUnitRow): number {
  return num(unit.stock_quantity);
}

/**
 * Nome de exibição de uma unidade estocável. Variação vira "Camiseta › P" — sem
 * o contexto do pai, "P" e "M" seriam duas linhas indistinguíveis na lista.
 * `parentName` vem do `productById` do `useStock`; sem ele cai no `name` da
 * própria linha (que, para uma variação, já é o nome do pai).
 */
export function stockUnitLabel(unit: StockUnitRow, parentName?: string | null): string {
  const base = String(parentName ?? unit.name ?? '').trim();
  const variant = String(unit.variant_name ?? '').trim();
  if (!variant) return base || 'Produto';
  if (!base) return variant;
  return `${base} › ${variant}`;
}

// ===== 1. Posição atual (fotografia de AGORA, não do período) =====

export interface StockPositionSummary {
  /** Unidades estocáveis (SKUs), não quantidade — un e kg não se somam. */
  trackedCount: number;
  /**
   * Abaixo (ou igual) ao mínimo — a MESMA conta de `filterLowStock`, para o
   * número bater com o alerta da tela /estoque. Sobrepõe-se de propósito com
   * zerados/negativos que tenham mínimo definido; a lista de alertas é que os
   * separa em baldes exclusivos.
   */
  lowCount: number;
  zeroCount: number;
  negativeCount: number;
  /**
   * Saldo positivo × preço de VENDA. É o potencial de venda do estoque, NÃO o
   * capital investido (para isso existe `investedValue`). Saldo negativo não
   * entra: não há mercadoria a vender.
   */
  salesValue: number;
  /**
   * Saldo positivo × CUSTO = capital investido (o que está imobilizado na
   * prateleira). Só entram unidades com custo informado — ausência de custo não
   * vira zero, senão o número sairia menor do que a realidade sem avisar.
   */
  investedValue: number;
  /**
   * Quantas unidades COM saldo positivo ficaram fora do `investedValue` por não
   * terem custo informado. É o que a tela mostra ("3 itens sem custo") para o
   * número nunca parecer completo quando não é.
   *
   * Saldo zerado/negativo sem custo não conta: ele somaria 0 de qualquer forma, e
   * avisar sobre item vazio inflaria o alerta sem informar nada.
   */
  missingCostCount: number;
}

export function stockPositionSummary(units: StockUnitRow[]): StockPositionSummary {
  let lowCount = 0;
  let zeroCount = 0;
  let negativeCount = 0;
  let salesValue = 0;
  let investedValue = 0;
  let missingCostCount = 0;

  for (const unit of units ?? []) {
    const balance = balanceOf(unit);
    if (isLowStock(unit)) lowCount += 1;
    if (balance === 0) zeroCount += 1;
    else if (balance < 0) negativeCount += 1;
    else {
      salesValue += balance * num(unit.price);
      // `productUnitCost` devolve null para ausência e preserva 0 (item doado é
      // custo zero de verdade) — `num()` não serviria aqui, ele achataria a
      // ausência em zero, que é exatamente o erro que este campo evita.
      const cost = productUnitCost(unit);
      if (cost == null) missingCostCount += 1;
      else investedValue += balance * cost;
    }
  }

  return {
    trackedCount: (units ?? []).length,
    lowCount,
    zeroCount,
    negativeCount,
    salesValue,
    investedValue,
    missingCostCount,
  };
}

// ===== 2. Movimentação no período, por motivo =====

export interface ReasonAggregate {
  reason: StockReason;
  /** Rótulo completo ("Baixa por orçamento"). */
  label: string;
  /** Rótulo curto para o eixo do gráfico. */
  shortLabel: string;
  /** Soma dos deltas POSITIVOS. */
  inQty: number;
  /** Soma do MÓDULO dos deltas negativos (sempre positiva). */
  outQty: number;
  /** inQty − outQty. */
  net: number;
  count: number;
}

/**
 * Entradas × saídas agrupadas por motivo. Só aparecem os motivos que de fato
 * ocorreram no período (um gráfico com 6 barras zeradas não informa nada), na
 * ordem canônica.
 *
 * `adjustment` é o único motivo que pode ser + ou − (ajuste por contagem sobe ou
 * desce o saldo), por isso a separação é pelo SINAL do delta, nunca pelo motivo.
 */
export function aggregateMovementsByReason(movements: StockMovementRow[]): ReasonAggregate[] {
  const buckets = new Map<string, { inQty: number; outQty: number; count: number }>();

  for (const m of movements ?? []) {
    const reason = String(m?.reason ?? '');
    if (!reason) continue;
    const delta = num(m.delta);
    const bucket = buckets.get(reason) ?? { inQty: 0, outQty: 0, count: 0 };
    if (delta >= 0) bucket.inQty += delta;
    else bucket.outQty += Math.abs(delta);
    bucket.count += 1;
    buckets.set(reason, bucket);
  }

  return Array.from(buckets.entries())
    .map(([reason, b]) => ({
      reason: reason as StockReason,
      // Motivo desconhecido (linha antiga/futura) mostra a chave crua em vez de
      // sumir do relatório.
      label: STOCK_REASON_LABELS[reason as StockReason] ?? reason,
      shortLabel: SHORT_REASON_LABELS[reason as StockReason] ?? reason,
      inQty: b.inQty,
      outQty: b.outQty,
      net: b.inQty - b.outQty,
      count: b.count,
    }))
    .sort((a, b) => {
      const ia = REASON_ORDER.indexOf(a.reason);
      const ib = REASON_ORDER.indexOf(b.reason);
      return (ia < 0 ? Number.MAX_SAFE_INTEGER : ia) - (ib < 0 ? Number.MAX_SAFE_INTEGER : ib)
        || a.label.localeCompare(b.label, 'pt-BR');
    });
}

export interface MovementTotals {
  totalIn: number;
  totalOut: number;
  /** totalIn − totalOut. */
  net: number;
  count: number;
}

/** Totais do período. Sem movimento algum → tudo 0 (nunca NaN). */
export function movementTotals(movements: StockMovementRow[]): MovementTotals {
  let totalIn = 0;
  let totalOut = 0;
  let count = 0;

  for (const m of movements ?? []) {
    const delta = num(m.delta);
    if (delta >= 0) totalIn += delta;
    else totalOut += Math.abs(delta);
    count += 1;
  }

  return { totalIn, totalOut, net: totalIn - totalOut, count };
}

// ===== 3. Mais movimentados (giro) =====

export interface MovedItem {
  productId: string;
  /** Já resolvido pelo chamador (inclui o contexto do pai nas variações). */
  name: string;
  /** Quantidade que SAIU (giro) — o número que ordena o ranking. */
  outQty: number;
  inQty: number;
  net: number;
  count: number;
}

/**
 * Top N por quantidade de SAÍDA no período — o coração do relatório para quem
 * vende produto. Itens que só tiveram entrada ficam de fora (giro zero não é
 * giro). `names` mapeia product_id → rótulo pronto; sem correspondência (produto
 * excluído depois do movimento) o item continua aparecendo como
 * "Produto removido", nunca some do total.
 */
export function topMovedItems(
  movements: StockMovementRow[],
  names: Map<string, string>,
  limit = 5,
): MovedItem[] {
  const buckets = new Map<string, { inQty: number; outQty: number; count: number }>();

  for (const m of movements ?? []) {
    const productId = m?.product_id ? String(m.product_id) : '';
    if (!productId) continue;
    const delta = num(m.delta);
    const bucket = buckets.get(productId) ?? { inQty: 0, outQty: 0, count: 0 };
    if (delta >= 0) bucket.inQty += delta;
    else bucket.outQty += Math.abs(delta);
    bucket.count += 1;
    buckets.set(productId, bucket);
  }

  return Array.from(buckets.entries())
    .filter(([, b]) => b.outQty > 0)
    .map(([productId, b]) => ({
      productId,
      name: names.get(productId) ?? 'Produto removido',
      outQty: b.outQty,
      inQty: b.inQty,
      net: b.inQty - b.outQty,
      count: b.count,
    }))
    .sort((a, b) => b.outQty - a.outQty || a.name.localeCompare(b.name, 'pt-BR'))
    .slice(0, limit);
}

// ===== 4. Estoque parado =====

/**
 * Unidades rastreadas SEM nenhum movimento no período — o recorte mais acionável
 * do relatório (capital parado) e o que a tela /estoque não mostra hoje.
 *
 * Ordenado pelo saldo (maior primeiro): quanto mais mercadoria parada, mais
 * urgente. Itens parados COM saldo zero ficam no fim — não custam nada.
 */
export function idleItems(units: StockUnitRow[], movements: StockMovementRow[]): StockUnitRow[] {
  const moved = new Set<string>();
  for (const m of movements ?? []) {
    if (m?.product_id) moved.add(String(m.product_id));
  }

  return (units ?? [])
    .filter((u) => !moved.has(String(u.id)))
    .sort(
      (a, b) =>
        balanceOf(b) - balanceOf(a) ||
        String(a.name ?? '').localeCompare(String(b.name ?? ''), 'pt-BR'),
    );
}

// ===== 5. Alertas =====

/**
 * Urgência de um alerta. `negative` é o mais grave (o saldo ficou devendo),
 * `zero` impede vender, `low` é aviso.
 */
export type StockAlertKind = 'negative' | 'zero' | 'low';

export const STOCK_ALERT_LABELS: Record<StockAlertKind, string> = {
  negative: 'Negativo',
  zero: 'Zerado',
  low: 'Abaixo do mínimo',
};

/**
 * Rótulo do SELO na lista. "Abaixo do mínimo" media ~130px e espremia o nome do
 * item ao lado — em device real a linha saiu "[TESTE-REL] Cami…", escondendo
 * justamente QUAL variação está baixa (com P/M/G, o nome truncado é inútil).
 * "Baixo" é também o termo que a tela /estoque já usa. A planilha continua com
 * o rótulo completo, onde a coluna tem largura própria.
 */
export const STOCK_ALERT_BADGE_LABELS: Record<StockAlertKind, string> = {
  negative: 'Negativo',
  zero: 'Zerado',
  low: 'Baixo',
};

export interface StockAlertRow {
  unit: StockUnitRow;
  kind: StockAlertKind;
  balance: number;
  /** `null` quando não há mínimo definido. */
  min: number | null;
}

/**
 * Situação de uma unidade — `null` quando está tudo certo. É a ESCADA de
 * urgência, e a única fonte de verdade dela: `alertBuckets` (tela) e
 * `buildStockXlsx` (planilha) chamam esta função, então a coluna "Situação" da
 * planilha nunca diverge do balde da tela.
 *
 * Zerado alerta mesmo SEM mínimo definido (não há o que vender); "abaixo do
 * mínimo" depende do mínimo e por isso passa por `isLowStock`, que trata
 * `min_stock` nulo como "sem alerta".
 */
export function alertKindOf(unit: StockUnitRow): StockAlertKind | null {
  const balance = balanceOf(unit);
  if (balance < 0) return 'negative';
  if (balance === 0) return 'zero';
  return isLowStock(unit) ? 'low' : null;
}

export interface StockAlertBuckets {
  negative: StockAlertRow[];
  zero: StockAlertRow[];
  low: StockAlertRow[];
  /** Os três baldes concatenados na ordem de urgência (negativo → zerado → baixo). */
  all: StockAlertRow[];
}

/**
 * Alertas em baldes EXCLUSIVOS: um item negativo com mínimo definido aparece uma
 * vez só, como `negative` (o mais grave). Sem isso a lista repetiria o mesmo
 * produto duas vezes e o usuário contaria errado.
 *
 * Um saldo zerado alerta mesmo SEM mínimo definido (não há o que vender);
 * `low` depende do mínimo e por isso passa por `isLowStock`, que trata
 * `min_stock` nulo como "sem alerta".
 */
export function alertBuckets(units: StockUnitRow[]): StockAlertBuckets {
  const negative: StockAlertRow[] = [];
  const zero: StockAlertRow[] = [];
  const low: StockAlertRow[] = [];

  for (const unit of units ?? []) {
    const kind = alertKindOf(unit);
    if (!kind) continue;
    const row: StockAlertRow = {
      unit,
      kind,
      balance: balanceOf(unit),
      min: unit.min_stock == null ? null : num(unit.min_stock),
    };
    if (kind === 'negative') negative.push(row);
    else if (kind === 'zero') zero.push(row);
    else low.push(row);
  }

  const bySeverity = (a: StockAlertRow, b: StockAlertRow) =>
    a.balance - b.balance ||
    String(a.unit.name ?? '').localeCompare(String(b.unit.name ?? ''), 'pt-BR');

  negative.sort(bySeverity);
  zero.sort(bySeverity);
  low.sort(bySeverity);

  return { negative, zero, low, all: [...negative, ...zero, ...low] };
}

// ===== Formatação compartilhada =====

/** Quantidade do relatório (pt-BR, até 3 casas). Espelha `formatStockQuantity`. */
export function formatQty(value: number): string {
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 3 }).format(num(value));
}
